/**
 * @file Controlled Drugs admin modal form field renderers.
 */

import { appData } from "../../app/state/state.js";
import {
  NATIVE_CHECKBOX_CLASSES,
  NATIVE_CHECKBOX_ICON_CLASSES,
  NATIVE_CHECKBOX_LABEL_CLASSES,
  NATIVE_CHECKBOX_MARKER_CLASSES,
} from "../../shared/forms/nativeFieldStyles.js";
import {
  escapeHTML,
} from "../../shared/utils/contentUtils.js";
import {
  DRUG_CONTROL_HELPER_TEXT_CLASSES,
  DRUG_MODAL_GRID_CLASSES,
  DRUG_MODAL_INPUT_CLASSES,
  DRUG_MODAL_SECTION_HEADING_CLASSES,
  getDrugNativeFieldHtml,
} from "./drugControlStyles.js";

const THRESHOLD_INPUT_CLASSES = [
  DRUG_MODAL_INPUT_CLASSES,
  "text-center",
].join(" ");

/**
 * Generates HTML for the Drug Inventory form.
 * @param {object} itemData Drug inventory item data.
 * @returns {string} Drug inventory form markup.
 */
export function getDrugInventoryFormFields(itemData) {
  const val = (field, def = "") => escapeHTML(String(itemData?.[field] ?? def));
  const getThresh = (loc, type) =>
    escapeHTML(String(itemData?.minStockThreshold?.[loc]?.[type] ?? ""));
  const locations = (appData?.config?.drugs?.locations || [])
    .filter((location) => location.isActive !== false);

  return `
    <h4 class="${DRUG_MODAL_SECTION_HEADING_CLASSES}">Drug Details</h4>
    <div class="${DRUG_MODAL_GRID_CLASSES} mb-6">
      ${getDrugNativeFieldHtml(
        "Drug Name",
        `<input type="text" name="name" value="${val("name")}" class="${DRUG_MODAL_INPUT_CLASSES}" placeholder="e.g. Morphine Sulphate" required>`,
        { className: "md:col-span-2", required: true },
      )}
      ${getDrugNativeFieldHtml(
        "Short Name",
        `<input type="text" name="shortName" value="${val("shortName")}" class="${DRUG_MODAL_INPUT_CLASSES}" placeholder="e.g. Morphine">`,
      )}
      ${getDrugNativeFieldHtml(
        "Pack Size (Amps)",
        `<input type="number" name="packSize" value="${val("packSize")}" class="${DRUG_MODAL_INPUT_CLASSES}" required>
        <p class="${DRUG_CONTROL_HELPER_TEXT_CLASSES}" data-native-field-skip="true">Reference only</p>`,
        { required: true },
      )}
      ${getDrugNativeFieldHtml(
        "Strength per Amp",
        `<input type="number" step="0.0001" name="strength" value="${val("strength")}" class="${DRUG_MODAL_INPUT_CLASSES}" required>`,
        { required: true },
      )}
      ${getDrugNativeFieldHtml(
        "Unit",
        `<input type="text" list="unit-options" name="strengthUnit" value="${val("strengthUnit", "mg")}" class="${DRUG_MODAL_INPUT_CLASSES}" placeholder="e.g. mg">
        <datalist id="unit-options">
          <option value="mg">
          <option value="mcg">
          <option value="g">
          <option value="ml">
        </datalist>`,
      )}
      ${itemData?.id ? `
      <div class="flex items-end pb-1">
        <label class="${NATIVE_CHECKBOX_LABEL_CLASSES}" data-native-checkbox-label>
          <input type="checkbox" name="isActive" class="${NATIVE_CHECKBOX_CLASSES}" ${itemData.isActive !== false ? "checked" : ""}>
          <span class="${NATIVE_CHECKBOX_MARKER_CLASSES}" data-native-checkbox-marker>
            <span class="${NATIVE_CHECKBOX_ICON_CLASSES} opacity-0" data-native-checkbox-icon>check</span>
          </span>
          <span>Active</span>
        </label>
      </div>` : ""}
    </div>

    <h4 class="${DRUG_MODAL_SECTION_HEADING_CLASSES} mt-4">Stock Alerts (Thresholds)</h4>
    <p class="${DRUG_CONTROL_HELPER_TEXT_CLASSES} mb-4">Set stock levels (in amps) that trigger alerts.</p>

    <div class="bg-gray-50 p-4 rounded-md dark:bg-gray-800 border dark:border-gray-700">
      <div class="grid grid-cols-12 gap-2 mb-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
        <div class="col-span-4">Location</div>
        <div class="col-span-4">Reminder</div>
        <div class="col-span-4">Warning</div>
      </div>

      <div class="grid grid-cols-12 gap-2 items-center mb-2">
        <div class="col-span-4 text-sm font-medium text-gray-700 dark:text-gray-300">Total Practice</div>
        <div class="col-span-4">
          <input type="number" name="threshold_total_reminder" value="${getThresh("total", "reminder")}" class="${THRESHOLD_INPUT_CLASSES}">
        </div>
        <div class="col-span-4">
          <input type="number" name="threshold_total_warning" value="${getThresh("total", "warning")}" class="${THRESHOLD_INPUT_CLASSES}">
        </div>
      </div>

      ${locations.map((location) => `
        <div class="grid grid-cols-12 gap-2 items-center mb-2">
          <div class="col-span-4 text-sm font-medium text-gray-700 dark:text-gray-300">${escapeHTML(location.name)}</div>
          <div class="col-span-4">
            <input type="number" name="threshold_${escapeHTML(location.id)}_reminder" value="${getThresh(location.id, "reminder")}" class="${THRESHOLD_INPUT_CLASSES}">
          </div>
          <div class="col-span-4">
            <input type="number" name="threshold_${escapeHTML(location.id)}_warning" value="${getThresh(location.id, "warning")}" class="${THRESHOLD_INPUT_CLASSES}">
          </div>
        </div>
      `).join("")}
    </div>
  `;
}

/**
 * Generates HTML for the Drug Role form.
 * @param {object} roleData Drug role data.
 * @returns {string} Drug role form markup.
 */
export function getDrugRoleFormFields(roleData) {
  const val = (field, def = "") => escapeHTML(String(roleData?.[field] ?? def));
  const perm = (permission) =>
    roleData?.permissions?.[permission] ? "checked" : "";

  const groups = [
    {
      title: "Visibility",
      perms: [
        { key: "viewDashboard", label: "View Stock" },
        { key: "viewHistory", label: "View History" },
        { key: "viewAudits", label: "View Audits" },
      ],
    },
    {
      title: "Actions",
      perms: [
        { key: "logUse", label: "Log Use" },
        { key: "restock", label: "Perform Restock" },
        { key: "relocate", label: "Relocate Stock" },
        { key: "correction", label: "Corrections" },
        { key: "logLoss", label: "Record Loss" },
        { key: "voidTransaction", label: "Void Own/Approved" },
        { key: "voidAnyTransaction", label: "Void ANY Transaction" },
      ],
    },
    {
      title: "Audit Management",
      perms: [
        { key: "startAudit", label: "Start Audit" },
        { key: "finalizeAudit", label: "Finalise Audit" },
        { key: "deleteAudit", label: "Delete Audit" },
      ],
    },
    {
      title: "Reporting",
      perms: [
        { key: "canExport", label: "Export Reports" },
      ],
    },
  ];

  return `
    <div class="space-y-4">
      <div class="grid grid-cols-1 gap-4 md:grid-cols-12">
        ${getDrugNativeFieldHtml(
          "Role Name",
          `<input type="text" name="name" value="${val("name")}" class="${DRUG_MODAL_INPUT_CLASSES}" placeholder="e.g. Audit Manager" required>`,
          { className: "md:col-span-12", required: true },
        )}
      </div>

      <div>
        <label class="block text-base text-gray-500 sm:text-sm mb-2">Permissions</label>
        <div class="space-y-3 bg-gray-50 p-4 rounded border dark:bg-gray-800 dark:border-gray-700 max-h-[60vh] overflow-y-auto">
          ${groups.map((group) => `
            <div>
              <h5 class="text-xs font-bold text-gray-500 uppercase tracking-wide border-b border-gray-200 pb-1 mb-2 dark:border-gray-600 dark:text-gray-400">
                ${group.title}
              </h5>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
                ${group.perms.map((permission) => `
                  <label class="${NATIVE_CHECKBOX_LABEL_CLASSES}" data-native-checkbox-label>
                    <input type="checkbox" name="perm_${permission.key}" ${perm(permission.key)} class="${NATIVE_CHECKBOX_CLASSES}">
                    <span class="${NATIVE_CHECKBOX_MARKER_CLASSES}" data-native-checkbox-marker>
                      <span class="${NATIVE_CHECKBOX_ICON_CLASSES} opacity-0" data-native-checkbox-icon>check</span>
                    </span>
                    <span>${permission.label}</span>
                  </label>
                `).join("")}
              </div>
            </div>
          `).join("")}

          <!-- Legacy Drug Admin Permission Removed (Used User Flag instead) -->
        </div>
      </div>
    </div>
  `;
}
